'use client';

import { useEffect, useRef, useState } from 'react';

import { stateManager } from './project-usability-recording-state-manager';
import { resetSilenceTimerOnRecordingChange } from './project-usability-speech-detection.helpers';

interface UseUsabilitySpeechDetectionOptions {
  isRecording: boolean;
  isSubmitting: boolean;
}

const SILENCE_THRESHOLD = 0.02; // Audio level threshold for detecting speech
const SILENCE_DURATION_MS = 1 * 60 * 1000; // 1 minutes
const PROMPT_DISPLAY_DURATION_MS = 10 * 1000; // 10 seconds before fade out
const SUSTAINED_SPEECH_DURATION_MS = 300; // Require speech above threshold for 300ms before considering it actual speech
const SUSTAINED_SILENCE_DURATION_MS = 500; // Require silence below threshold for 500ms before considering silence started
const SMOOTHING_WINDOW_SIZE = 5; // Number of samples to average for smoothing

export function useUsabilitySpeechDetection({
  isRecording,
  isSubmitting,
}: UseUsabilitySpeechDetectionOptions) {
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const silenceStartTimeRef = useRef<number | null>(null);
  const promptLastHiddenTimeRef = useRef<number | null>(null);
  const fadeOutTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const recheckTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const showPromptRef = useRef(false);
  const isFadingOutRef = useRef(false);
  const audioLevelHistoryRef = useRef<number[]>([]);
  const speechStartTimeRef = useRef<number | null>(null);
  const silenceStartTrackingRef = useRef<number | null>(null);
  const isCurrentlySpeakingRef = useRef(false);
  const isSubmittingRef = useRef(isSubmitting);

  const [showPrompt, setShowPrompt] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [lastIsRecording, setLastIsRecording] = useState(isRecording);

  if (lastIsRecording !== isRecording) {
    setLastIsRecording(isRecording);
    if (!isRecording) {
      setShowPrompt(false);
      setIsFadingOut(false);
    }
  }

  useEffect(
    () =>
      resetSilenceTimerOnRecordingChange({
        isSubmitting,
        isRecording,
        silenceStartTimeRef,
      }),
    [isSubmitting, isRecording]
  );

  useEffect(() => {
    if (!isRecording) {
      silenceStartTimeRef.current = null;
      promptLastHiddenTimeRef.current = null;
      audioLevelHistoryRef.current = [];
      speechStartTimeRef.current = null;
      silenceStartTrackingRef.current = null;
      isCurrentlySpeakingRef.current = false;
      if (fadeOutTimeoutRef.current) {
        clearTimeout(fadeOutTimeoutRef.current);
        fadeOutTimeoutRef.current = null;
      }
      if (recheckTimeoutRef.current) {
        clearTimeout(recheckTimeoutRef.current);
        recheckTimeoutRef.current = null;
      }
      return;
    }

    const micStream = stateManager.getMicStream();
    if (!micStream) {
      return;
    }

    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    const audioContext = new AudioContextClass();

    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.8;

    const source = audioContext.createMediaStreamSource(micStream);
    source.connect(analyser);

    audioContextRef.current = audioContext;
    analyserRef.current = analyser;
    sourceRef.current = source;

    // Initialize silence start time to now (we start in silence)
    silenceStartTimeRef.current = Date.now();
    isCurrentlySpeakingRef.current = false;

    const dataArray = new Uint8Array(analyser.fftSize);

    audioLevelHistoryRef.current = [];
    speechStartTimeRef.current = null;
    silenceStartTrackingRef.current = null;

    const checkAudioLevel = () => {
      if (!analyserRef.current || !isRecording) {
        return;
      }

      analyserRef.current.getByteTimeDomainData(dataArray);

      // Calculate average audio level
      let sum = 0;
      for (let i = 0; i < dataArray.length; i++) {
        const normalized = (dataArray[i] - 128) / 128;
        sum += Math.abs(normalized);
      }
      const rawLevel = sum / dataArray.length;

      // Add to history for smoothing
      audioLevelHistoryRef.current.push(rawLevel);
      if (audioLevelHistoryRef.current.length > SMOOTHING_WINDOW_SIZE) {
        audioLevelHistoryRef.current.shift();
      }

      // Calculate smoothed average
      const smoothedSum = audioLevelHistoryRef.current.reduce(
        (acc, val) => acc + val,
        0
      );
      const normalizedLevel = smoothedSum / audioLevelHistoryRef.current.length;

      const now = Date.now();

      // If we're submitting, skip logic and ensure silence timer is null
      if (isSubmittingRef.current) {
        if (silenceStartTimeRef.current !== null) {
          silenceStartTimeRef.current = null;
        }
        if (isRecording) {
          animationFrameRef.current = requestAnimationFrame(checkAudioLevel);
        }
        return;
      }

      // Determine if we're in speech or silence
      if (normalizedLevel > SILENCE_THRESHOLD) {
        // Audio above threshold
        if (isCurrentlySpeakingRef.current) {
          // Already speaking - continue speaking state
          // Do nothing, silenceStartTimeRef remains unchanged
        } else {
          // Not currently speaking - check if this is sustained speech
          if (speechStartTimeRef.current === null) {
            // Start tracking potential speech
            speechStartTimeRef.current = now;
          } else {
            // Check if speech has been sustained long enough
            const speechDuration = now - speechStartTimeRef.current;
            if (speechDuration >= SUSTAINED_SPEECH_DURATION_MS) {
              // Confirmed speech - transition to speaking state
              isCurrentlySpeakingRef.current = true;
              speechStartTimeRef.current = null;
              silenceStartTrackingRef.current = null;

              // Hide prompt if showing
              if (showPromptRef.current) {
                showPromptRef.current = false;
                isFadingOutRef.current = false;
                setShowPrompt(false);
                setIsFadingOut(false);
                if (fadeOutTimeoutRef.current) {
                  clearTimeout(fadeOutTimeoutRef.current);
                  fadeOutTimeoutRef.current = null;
                }
                if (recheckTimeoutRef.current) {
                  clearTimeout(recheckTimeoutRef.current);
                  recheckTimeoutRef.current = null;
                }
              }
            }
          }
        }
      } else {
        // Audio below threshold
        if (isCurrentlySpeakingRef.current) {
          // Was speaking, now below threshold - check if this is sustained silence
          if (silenceStartTrackingRef.current === null) {
            // Start tracking potential silence
            silenceStartTrackingRef.current = now;
          } else {
            // Check if silence has been sustained long enough
            const silenceDuration = now - silenceStartTrackingRef.current;
            if (silenceDuration >= SUSTAINED_SILENCE_DURATION_MS) {
              // Confirmed silence - transition to silence state
              isCurrentlySpeakingRef.current = false;
              silenceStartTimeRef.current = now; // Mark when silence actually started
              speechStartTimeRef.current = null;
              silenceStartTrackingRef.current = null;
            }
          }
        } else {
          // Already in silence - reset speech tracking
          if (speechStartTimeRef.current !== null) {
            speechStartTimeRef.current = null;
          }
        }
      }

      // Check if we should show the prompt
      if (!isCurrentlySpeakingRef.current && silenceStartTimeRef.current) {
        const silenceDuration = now - silenceStartTimeRef.current;

        // Only show prompt if:
        // 1. Silence duration >= 30 seconds
        // 2. Prompt is not already showing
        // 3. Prompt is not fading out
        // 4. At least 30 seconds have passed since prompt was last hidden
        const timeSinceLastHidden = promptLastHiddenTimeRef.current
          ? now - promptLastHiddenTimeRef.current
          : Infinity;

        if (
          silenceDuration >= SILENCE_DURATION_MS &&
          !showPromptRef.current &&
          !isFadingOutRef.current &&
          timeSinceLastHidden >= SILENCE_DURATION_MS
        ) {
          showPromptRef.current = true;
          isFadingOutRef.current = false;
          setShowPrompt(true);
          setIsFadingOut(false);

          // Set fade out after 10 seconds
          fadeOutTimeoutRef.current = setTimeout(() => {
            isFadingOutRef.current = true;
            setIsFadingOut(true);

            // After fade out completes, hide prompt and mark when it was hidden
            setTimeout(() => {
              showPromptRef.current = false;
              isFadingOutRef.current = false;
              setShowPrompt(false);
              setIsFadingOut(false);

              // Mark when prompt was hidden - must wait 30 seconds before showing again
              promptLastHiddenTimeRef.current = Date.now();
            }, 300); // Fade out duration
          }, PROMPT_DISPLAY_DURATION_MS);
        }
      }

      if (isRecording) {
        animationFrameRef.current = requestAnimationFrame(checkAudioLevel);
      }
    };

    animationFrameRef.current = requestAnimationFrame(checkAudioLevel);

    // When the tab returns to the foreground, some browsers (or power-saver
    // modes) may have suspended the AudioContext and paused rAF. Resume the
    // context and restart the silence window so we don't immediately fire the
    // prompt based on stale time kept while the tab was hidden.
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      if (audioContextRef.current?.state === 'suspended') {
        audioContextRef.current.resume().catch(() => {
          // ignore
        });
      }
      silenceStartTimeRef.current = Date.now();
      speechStartTimeRef.current = null;
      silenceStartTrackingRef.current = null;
      audioLevelHistoryRef.current = [];
      if (animationFrameRef.current === null) {
        animationFrameRef.current = requestAnimationFrame(checkAudioLevel);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      if (fadeOutTimeoutRef.current) {
        clearTimeout(fadeOutTimeoutRef.current);
        fadeOutTimeoutRef.current = null;
      }
      if (recheckTimeoutRef.current) {
        clearTimeout(recheckTimeoutRef.current);
        recheckTimeoutRef.current = null;
      }
      if (sourceRef.current) {
        sourceRef.current.disconnect();
        sourceRef.current = null;
      }
      if (analyserRef.current) {
        analyserRef.current.disconnect();
        analyserRef.current = null;
      }
      if (audioContextRef.current) {
        void audioContextRef.current.close();
        audioContextRef.current = null;
      }
      showPromptRef.current = false;
      isFadingOutRef.current = false;
      setShowPrompt(false);
      setIsFadingOut(false);
    };
  }, [isRecording]);

  useEffect(() => {
    isSubmittingRef.current = isSubmitting;
  });

  return {
    showPrompt,
    isFadingOut,
    onClose: () => setShowPrompt(false),
  };
}
